import type { EditorHistoryCommand } from '@/editor/history/types'
import { getMockupHistoryBridge } from '@/editor/history/mockupEditorBridge'
import type { ScreenMediaType } from '@/types/animationLayer'

/**
 * 修改图层名称。
 */
export class SetLayerNameCommand implements EditorHistoryCommand {
  readonly label = 'Rename layer'

  private readonly layerId: string

  private readonly before: string

  private readonly after: string

  /**
   * @param layerId - 图层 id
   * @param before - 旧名称
   * @param after - 新名称
   */
  constructor(layerId: string, before: string, after: string) {
    this.layerId = layerId
    this.before = before
    this.after = after
  }

  /** @inheritdoc */
  revert(): void {
    const layer = getMockupHistoryBridge().animationLayers.value.find((l) => l.id === this.layerId)
    if (layer) layer.name = this.before
  }

  /** @inheritdoc */
  redo(): void {
    const layer = getMockupHistoryBridge().animationLayers.value.find((l) => l.id === this.layerId)
    if (layer) layer.name = this.after
  }
}

/**
 * 修改图层设备模板 id。
 */
export class SetLayerDeviceTemplateCommand implements EditorHistoryCommand {
  readonly label = 'Change device template'

  private readonly layerId: string

  private readonly before: string

  private readonly after: string

  /**
   * @param layerId - 图层 id
   * @param before - 旧模板 id
   * @param after - 新模板 id
   */
  constructor(layerId: string, before: string, after: string) {
    this.layerId = layerId
    this.before = before
    this.after = after
  }

  /** @inheritdoc */
  revert(): void {
    const layer = getMockupHistoryBridge().animationLayers.value.find((l) => l.id === this.layerId)
    if (layer) layer.deviceTemplateId = this.before
  }

  /** @inheritdoc */
  redo(): void {
    const layer = getMockupHistoryBridge().animationLayers.value.find((l) => l.id === this.layerId)
    if (layer) layer.deviceTemplateId = this.after
  }
}

/** 多图栈在撤销栈中的一帧快照（与运行时 `ScreenImageStackItem` 对齐）。 */
export type ScreenImageSnapshotItem = {
  id: string
  fileName: string
  objectUrl: string | null
}

/**
 * 屏幕贴图引用（文件名、对象 URL、媒体类型、视频偏移、多图栈）。
 */
export type ScreenMaterialSnapshot = {
  fileName: string | null
  objectUrl: string | null
  mediaType: ScreenMediaType
  offsetSec: number
  screenImages: ScreenImageSnapshotItem[]
}

/**
 * 修改屏幕材质 / 资源引用。
 */
export class SetScreenMaterialCommand implements EditorHistoryCommand {
  readonly label = 'Screen material'

  private readonly layerId: string

  private readonly before: ScreenMaterialSnapshot

  private readonly after: ScreenMaterialSnapshot

  /**
   * @param layerId - 图层 id
   * @param before - 变更前
   * @param after - 变更后
   */
  constructor(layerId: string, before: ScreenMaterialSnapshot, after: ScreenMaterialSnapshot) {
    this.layerId = layerId
    this.before = before
    this.after = after
  }

  /** @inheritdoc */
  revert(): void {
    this.apply(this.before)
  }

  /** @inheritdoc */
  redo(): void {
    this.apply(this.after)
  }

  /**
   * @param s - 目标快照
   */
  private apply(s: ScreenMaterialSnapshot): void {
    const layer = getMockupHistoryBridge().animationLayers.value.find((l) => l.id === this.layerId)
    if (!layer) return
    const nextStackUrls = new Set(s.screenImages.map((i) => i.objectUrl).filter(Boolean) as string[])
    if (layer.screenTextureSrc && layer.screenTextureSrc !== s.objectUrl) {
      try {
        URL.revokeObjectURL(layer.screenTextureSrc)
      } catch {
        /* ignore */
      }
    }
    for (const it of layer.screenImages) {
      const u = it.textureSrc
      if (u && u !== s.objectUrl && !nextStackUrls.has(u)) {
        try {
          URL.revokeObjectURL(u)
        } catch {
          /* ignore */
        }
      }
    }
    layer.screenMaterialFileName = s.fileName
    layer.screenTextureSrc = s.objectUrl
    layer.screenMediaType = s.mediaType
    layer.screenVideoTimelineOffsetSec = s.offsetSec
    layer.screenImages = (s.screenImages ?? []).map((it) => ({
      id: it.id,
      fileName: it.fileName,
      textureSrc: it.objectUrl,
    }))
  }

  /** @inheritdoc */
  dispose(reason: 'evicted' | 'cleared'): void {
    void reason
    const liveLayers = getMockupHistoryBridge().animationLayers.value
    const urlStillUsed = (url: string): boolean =>
      liveLayers.some(
        (l) => l.screenTextureSrc === url || l.screenImages.some((si) => si.textureSrc === url),
      )
    const revokeIfOrphan = (url: string | null): void => {
      if (!url) return
      if (!urlStillUsed(url)) {
        try {
          URL.revokeObjectURL(url)
        } catch {
          /* ignore */
        }
      }
    }
    const collect = (s: ScreenMaterialSnapshot): void => {
      revokeIfOrphan(s.objectUrl)
      for (const it of s.screenImages ?? []) revokeIfOrphan(it.objectUrl)
    }
    collect(this.before)
    collect(this.after)
  }
}

/**
 * 视频时间轴相对偏移（秒）。
 */
export class SetScreenVideoOffsetCommand implements EditorHistoryCommand {
  readonly label = 'Video timeline offset'

  private readonly layerId: string

  private readonly before: number

  private readonly after: number

  /**
   * @param layerId - 图层 id
   * @param before - 旧值
   * @param after - 新值
   */
  constructor(layerId: string, before: number, after: number) {
    this.layerId = layerId
    this.before = before
    this.after = after
  }

  /** @inheritdoc */
  revert(): void {
    const layer = getMockupHistoryBridge().animationLayers.value.find((l) => l.id === this.layerId)
    if (layer) layer.screenVideoTimelineOffsetSec = this.before
  }

  /** @inheritdoc */
  redo(): void {
    const layer = getMockupHistoryBridge().animationLayers.value.find((l) => l.id === this.layerId)
    if (layer) layer.screenVideoTimelineOffsetSec = this.after
  }
}
